'use client';

import { SelectProvider } from '@magicschool/spellbook/components/Select/SelectProvider';
import {
  SelectContentSlot,
  SelectGroupSlot,
  SelectLeadingAddOnSlot,
  SelectLeadingAdornmentSlot,
  SelectOptionSlot,
  SelectTrailingAddOnSlot,
  SelectTriggerSlot,
} from '@magicschool/spellbook/components/Select/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as SelectPrimitive from 'radix-ui/select';
import { forwardRef } from 'react';

export type SelectProps = Omit<React.ComponentProps<typeof SelectPrimitive.Root>, 'dir'> &
  Omit<VariantProps<typeof selectVariants>, 'invalid'> & {
    /** Applies error border styling. Pass as a boolean, not the string "true". Pair with `aria-describedby` pointing to an error message element. */
    invalid?: boolean;
    className?: string;
    /** The id attribute for the select trigger, used to associate with a label via `htmlFor`. */
    id?: string;
    /** The id of an external label element. Sets `aria-labelledby` on the trigger for accessibility. */
    labelId?: string;
    /** Text displayed when no option is selected. Also used as `aria-label` fallback if no label is provided. */
    placeholder?: string;
    /** When true, displays the selected value but prevents interaction. */
    readOnly?: boolean;
    /** Layout direction for option descriptions: `horizontal` (inline) or `vertical` (stacked). */
    descriptionOrientation?: 'horizontal' | 'vertical';
    /** Callback when the dropdown closes and focus returns to the trigger. */
    onCloseAutoFocus?: (event: Event) => void;
    /** Callback when Escape is pressed while the dropdown is open. */
    onEscapeKeyDown?: (event: KeyboardEvent) => void;
    /** Callback when clicking outside the dropdown. */
    onPointerDownOutside?: (event: CustomEvent<{ originalEvent: PointerEvent }>) => void;
    /** Callback when the select trigger gains focus. */
    onFocus?: React.FocusEventHandler<HTMLDivElement>;
    /** Callback when the select trigger loses focus. */
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    /** Accessible label when no visible label is present. Prefer using `labelId` or `Form.Group` with `Form.Label` instead. */
    'aria-label'?: string;
    /** Ids of elements that describe the field, such as helper or error text. Set on the focusable trigger, not the popup. */
    'aria-describedby'?: string;
    'data-testid'?: string;
  };

const selectVariants = cva(
  [
    'flex',
    'items-center',
    'justify-start',
    'w-full',
    'sm:min-w-form-field-min',
    'border',
    'text-sm',
    'font-normal',
    'transition-all',
    'outline-none',
    'data-[state=open]:border-focus-ring',
    'has-[[data-state=open]]:border-focus-ring',
  ],
  {
    variants: {
      /** The size of the select. */
      size: {
        md: ['h-control-h-md', 'rounded-control-md'],
        lg: ['h-control-h-lg', 'rounded-control-lg'],
        xl: ['h-control-h-xl', 'rounded-control-xl'],
      },
      /** Whether the select is read-only. */
      readOnly: {
        true: [
          'bg-surface-background-1',
          'border-border-default',
          'hover:border-border-default',
          'focus-within:border-border-default',
          'hover:focus-within:border-border-default',
        ],
        false: [
          'bg-surface-background-0',
          'border-border-strong',
          'hover:border-border-strong-hover',
          'focus-within:border-focus-ring',
          'hover:focus-within:border-focus-ring',
        ],
      },
      /** Whether the select is disabled. */
      disabled: {
        true: [
          'cursor-not-allowed',
          'border-disabled-background',
          'hover:border-disabled-background',
          'focus-within:border-disabled-background',
          'bg-disabled-background',
          'hover:bg-disabled-background',
        ],
        false: '',
      },
      /** Whether the field is invalid */
      invalid: {
        true: [
          /**
           * @TODO [SPE-105] Centralize "invalid"/state color tokens to single utility
           * https://linear.app/magicschool/issue/SPE-105/centralize-invalid-state-color-tokens
           */
          'border-error-stroke',
          'hover:border-error-stroke',
          'focus-within:border-error-stroke',
          'hover:focus-within:border-error-stroke',
          'data-[state=open]:border-error-stroke',
          'has-[[data-state=open]]:border-error-stroke',
        ],
      },
    },
    compoundVariants: [
      {
        invalid: true,
        disabled: true,
        className: 'border-disabled-background hover:border-disabled-background focus-within:border-disabled-background',
      },
    ],
  },
);

const selectLeadingAddOnVariants = cva(
  [
    'items-center',
    'justify-center',
    'inline-flex',
    'shrink-0',
    'h-full',
    'ltr:border-r-1',
    'ltr:border-r-border-strong',
    'rtl:border-l-1',
    'rtl:border-l-border-strong',
  ],
  {
    variants: {
      size: {
        md: ['px-2'],
        lg: ['px-3'],
        xl: ['px-3'],
      },
      disabled: {
        true: ['ltr:border-r-disabled-background', 'rtl:border-l-disabled-background'],
        false: '',
      },
    },
  },
);

const selectTrailingAddOnVariants = cva(
  [
    'items-center',
    'justify-center',
    'inline-flex',
    'shrink-0',
    'h-full',
    'ltr:border-l-1',
    'ltr:border-l-border-strong',
    'rtl:border-r-1',
    'rtl:border-r-border-strong',
  ],
  {
    variants: {
      size: {
        md: ['px-2'],
        lg: ['px-3'],
        xl: ['px-3'],
      },
      disabled: {
        true: ['ltr:border-l-disabled-background', 'rtl:border-r-disabled-background'],
        false: '',
      },
    },
  },
);

const SelectBase = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      children,
      className,
      id,
      labelId,
      size = 'md',
      disabled = false,
      readOnly = false,
      invalid = false,
      placeholder,
      descriptionOrientation = 'horizontal',
      value,
      defaultValue,
      onValueChange,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocus,
      onBlur,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      ...props
    },
    forwardedRef,
  ) => {
    const { slots } = useSlots(children, {
      leadingAddOn: { Component: SelectLeadingAddOnSlot },
      trailingAddOn: { Component: SelectTrailingAddOnSlot },
      content: {
        Component: SelectContentSlot,
        props: {
          size,
          id,
          labelId,
          placeholder,
          descriptionOrientation,
          invalid: invalid === true,
          'aria-label': ariaLabel,
          'data-testid': dataTestId,
          disabled: disabled === true,
          readOnly: readOnly === true,
          children,
          ref: forwardedRef,
          ...props,
        },
      },
    });

    return (
      <SelectProvider
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        onCloseAutoFocus={onCloseAutoFocus}
        onEscapeKeyDown={onEscapeKeyDown}
        onPointerDownOutside={onPointerDownOutside}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <div data-spellbook-name="select" className={cn(selectVariants({ size, disabled, readOnly, invalid }), className)}>
          {slots.leadingAddOn && <span className={selectLeadingAddOnVariants({ size, disabled })}>{slots.leadingAddOn}</span>}
          {slots.content}
          {slots.trailingAddOn && <span className={selectTrailingAddOnVariants({ size, disabled })}>{slots.trailingAddOn}</span>}
        </div>
      </SelectProvider>
    );
  },
);

/**
 * A dropdown select for choosing a single option from a predefined list.
 *
 * ### When to use
 * - Selecting one option from 5-15 predefined choices
 * - When all options can be shown in a scrollable dropdown
 * - Form fields requiring a single selection from a known set
 * - Settings or configuration with discrete choices
 *
 * ### When NOT to use
 * - For 2-4 options where all can be visible: use `RadioGroup` instead
 * - For binary yes/no choices: use `Switch` or `Checkbox` instead
 * - For searchable/filterable lists: use `Autocomplete` instead
 * - For selecting multiple options: use `MultiSelect` instead
 * - For very long lists (50+ options): use `Autocomplete` with search
 * - For action menus (edit, delete): use `DropdownMenu` instead
 *
 * ### Selection component comparison
 * | Component | Selection | Search | Trigger | Use case |
 * |-----------|-----------|--------|---------|----------|
 * | **Select** | Single | No | Built-in | 5-15 static options, form fields |
 * | **Autocomplete** | Single | Yes | Built-in | 15+ options, API search |
 * | **MultiSelect** | Multiple | Yes | Built-in | Multiple selections with chips |
 * | **DropdownMenu** | Actions | No | Built-in | Action menus, context menus |
 * | **Command** | Single/Multi | Yes | **Manual** | Complex search UIs, custom composition |
 *
 * ### API
 * - `value` / `onValueChange`: Controlled state for the selected option
 * - `defaultValue`: Uncontrolled initial selection
 * - `placeholder`: Text shown when no option is selected
 * - `size`: Field height (`md` default, `lg` for prominent selects, `xl` for extra-large selects)
 * - `disabled`: Prevents interaction
 * - `readOnly`: Shows value but prevents changes
 * - `invalid`: Error state styling
 * - `labelId`: Associates with an external label for accessibility
 *
 * ### Composition (slots)
 * - `<Select.Option>`: A selectable option. Pass `value` (required) and `label`. Optional `description` and `disabled`.
 * - `<Select.Group>`: Groups related options with a label header.
 * - `<Select.LeadingAddOn>`: Content before the field with a border separator (e.g., currency label).
 * - `<Select.LeadingAdornment>`: Icon before the selected value.
 * - `<Select.TrailingAddOn>`: Content after the field with a border separator (e.g., unit label).
 *
 * ### Visual states
 * - **Default**: Standard border
 * - **Hover**: Border highlight
 * - **Focus/Open**: Focus ring, chevron rotates
 * - **Invalid**: Error border color
 * - **Disabled**: Muted background, no interaction
 * - **Read-only**: Shows value without dropdown trigger
 *
 * ### Accessibility
 * - Uses native keyboard navigation (arrow keys, type-ahead, Enter, Escape)
 * - Screen readers announce the selected value and available options
 *
 * ### Label association
 * This is an atomic component decoupled from labels. Associate a label using one of:
 * - `labelId`: Pass the ID of an external `<Label>` element
 * - `aria-label`: For visually hidden labels
 * - `id` + `<Label htmlFor={id}>`: Standard HTML label association
 */
export const Select = Object.assign(SelectBase, {
  Trigger: SelectTriggerSlot,
  LeadingAddOn: SelectLeadingAddOnSlot,
  LeadingAdornment: SelectLeadingAdornmentSlot,
  TrailingAddOn: SelectTrailingAddOnSlot,
  Option: SelectOptionSlot,
  Content: SelectContentSlot,
  Group: SelectGroupSlot,
});
